import { css } from '@emotion/core';
import { ComponentProps } from 'react';
import styled from '@emotion/styled';
import isPropValid from '@emotion/is-prop-valid';
import { getCommonButtonStyles, ITheme, themed } from '@/ui';
import { ButtonLabel, IButtonBaseProps } from './Button';
import { RouterLink } from '../link/RouterLink';

interface IProps
	extends ComponentProps<typeof RouterLink>,
		Omit<IButtonBaseProps, 'buttonType'> {
	buttonType?: 'primary' | 'secondary' | 'text';
	inverted?: boolean;
}

export default function LinkButton({ children, icon, ...props }: IProps) {
	return (
		<StyledLink {...props}>
			<ButtonLabel>{children}</ButtonLabel>
			{icon}
		</StyledLink>
	);
}

const StyledLink = styled(RouterLink, { shouldForwardProp: isPropValid })<
	IProps,
	ITheme
>`
	${({ inverted, buttonType, iconPosition, theme }) => {
		if (buttonType === 'text') {
			return css`
				color: ${theme.color.brand};

				svg {
					width: 24px;
					height: 24px;
					transition: transform 0.3s ease;
				}

				&:not([aria-disabled='true']):hover svg {
					transform: translateX(${iconPosition === 'left' ? -8 : 8}px);
				}
			`;
		}

		const secondary = buttonType === 'secondary';
		const invertedSecondary = secondary && inverted;

		return css`
			${getCommonButtonStyles(
				secondary ? 'secondary' : 'primary',
				theme,
				inverted,
				invertedSecondary,
			)}

			border-radius: ${theme.button.radius};
			padding: ${theme.button.padding};
			height: ${theme.button.height};

			${secondary &&
			`
				&:not([aria-disabled='false']) {
					color: ${theme.button.secondary.disabled.font.color};
					border: ${theme.button.secondary.disabled.border};
					svg {
						path {
							fill: ${theme.button.secondary.disabled.font.color};
						}
					}

					&:hover {
						background-color: transparent;
						box-shadow: none;
						border: ${theme.button.secondary.disabled.border};
						color: ${theme.button.secondary.disabled.font.color};
					}
				}
				`}
		`;
	}}

	line-height: ${themed(({ button }) => button.lineHeight)};
	gap: ${themed(({ button }) => button.iconSeparator)};
	display: inline-flex;
	flex-direction: ${({ iconPosition }) =>
		iconPosition === 'left' ? 'row-reverse' : 'row'};
	align-items: center;
	text-decoration: none;
	font-weight: 500;

	& svg path {
		fill: currentColor;
	}
`;
